// Reusable Pipeline Workflows dashboard.
//
// Two sections:
//   1. Matrix — 10 canonical stages × 8 tier lanes with live person counts
//                pulled from `GET /api/pipedrive/counts`.
//   2. Lane cards — one card per Pipedrive pipeline with per-stage counts.
//
// Also exposes the "PD SYNC" button that clears local drag-drop overrides
// (POST /api/pipedrive/sync). Because the Deal / Lead pipelines already
// resolve `override || pd_derived`, wiping overrides IS the sync.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { Loader2, RefreshCw, GitBranch, Workflow, AlertTriangle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { TIER_TONE, SHORT_TIER } from "../../constants/pipeline";

// Compact label for the matrix column header. Full tier name is a long
// string ("PLATINUM"), so we clip for the header cell.
const shortTier = (t) => SHORT_TIER[t] || t;

// Colour intensity for a matrix cell based on count.
function cellIntensity(count) {
  if (!count) return "text-slate-700";
  if (count < 5) return "text-slate-300";
  if (count < 25) return "text-orange-300 font-semibold";
  return "text-orange-200 font-bold";
}

function StageMatrix({ counts }) {
  const stageLabelByKey = useMemo(() => {
    const map = {};
    (counts?.pipelines || []).forEach((p) => {
      (p.stages || []).forEach((s) => {
        if (s.canonical_key && !map[s.canonical_key]) map[s.canonical_key] = s.name;
      });
    });
    return map;
  }, [counts]);

  if (!counts) return null;
  const { matrix, tier_axis, stage_keys, stage_ids_by_cell = {}, pipeline_id_by_tier = {} } = counts;
  // Comma-joined list of Pipedrive stage IDs for a (stage_key, tier) cell.
  const cellIds = (sk, t) => (stage_ids_by_cell?.[sk]?.[t] || []).join(", ");

  // Row totals: sum across all tiers.
  const rowTotal = (sk) => tier_axis.reduce((acc, t) => acc + (matrix[sk]?.[t] || 0), 0);
  const colTotal = (t) => stage_keys.reduce((acc, sk) => acc + (matrix[sk]?.[t] || 0), 0)
    + (matrix._unmapped?.[t] || 0);

  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#111C34] overflow-hidden" data-testid="workflow-matrix">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[880px]">
          <thead>
            <tr className="bg-white/[0.04] border-b border-white/[0.08]">
              <th className="text-left px-3 py-3 text-[10.5px] font-extrabold uppercase tracking-widest text-[#2FC8CB] font-mono w-52">
                Canonical stage
              </th>
              {tier_axis.map((t) => (
                <th key={t} className={`px-2 py-3 text-center text-[10.5px] font-extrabold uppercase tracking-widest font-mono ${TIER_TONE[t] || TIER_TONE.UNMAPPED} border-l border-white/[0.04]`}>
                  <div>{shortTier(t)}</div>
                  {pipeline_id_by_tier[t] != null && (
                    <div className="text-[8.5px] opacity-70 font-normal mt-0.5">PL #{pipeline_id_by_tier[t]}</div>
                  )}
                </th>
              ))}
              <th className="px-3 py-3 text-right text-[10.5px] font-extrabold uppercase tracking-widest text-slate-500 font-mono w-16">Σ</th>
            </tr>
          </thead>
          <tbody>
            {stage_keys.map((sk) => (
              <tr key={sk} className="border-b border-white/[0.04] hover:bg-white/[0.03]">
                <td className="px-3 py-2.5">
                  <div className="text-white font-medium text-[13px]">{stageLabelByKey[sk] || sk}</div>
                  <div className="text-[9.5px] font-mono uppercase tracking-widest text-slate-500">{sk}</div>
                </td>
                {tier_axis.map((t) => {
                  const c = matrix[sk]?.[t] || 0;
                  const ids = cellIds(sk, t);
                  return (
                    <td key={t} className={`px-2 py-2.5 text-center tabular-nums border-l border-white/[0.04] ${cellIntensity(c)}`}
                        data-testid={`matrix-${sk}-${t}`}
                        title={ids ? `Pipedrive stage IDs: ${ids}` : ""}>
                      <div>{c || "·"}</div>
                      {ids && (
                        <div className="text-[9px] font-mono text-slate-600 mt-0.5 font-normal">#{ids}</div>
                      )}
                    </td>
                  );
                })}
                <td className="px-3 py-2.5 text-right tabular-nums font-mono text-slate-400">{rowTotal(sk)}</td>
              </tr>
            ))}
            {/* Unmapped-stage row: persons with PipelineId but no StageId */}
            <tr className="border-b border-white/[0.04] bg-white/[0.02]">
              <td className="px-3 py-2.5">
                <div className="text-slate-300 font-medium text-[13px]">Unmapped stage</div>
                <div className="text-[9.5px] font-mono uppercase tracking-widest text-slate-500">no StageId</div>
              </td>
              {tier_axis.map((t) => {
                const c = matrix._unmapped?.[t] || 0;
                return (
                  <td key={t} className={`px-2 py-2.5 text-center tabular-nums border-l border-white/[0.04] ${cellIntensity(c)}`}>
                    {c || "·"}
                  </td>
                );
              })}
              <td className="px-3 py-2.5 text-right tabular-nums font-mono text-slate-400">
                {tier_axis.reduce((a, t) => a + (matrix._unmapped?.[t] || 0), 0)}
              </td>
            </tr>
            {/* Column totals */}
            <tr className="bg-white/[0.04]">
              <td className="px-3 py-2.5 text-[10.5px] font-mono uppercase tracking-widest text-slate-500">Total per tier</td>
              {tier_axis.map((t) => (
                <td key={t} className="px-2 py-2.5 text-center tabular-nums font-mono text-white font-semibold border-l border-white/[0.04]">
                  {colTotal(t)}
                </td>
              ))}
              <td className="px-3 py-2.5 text-right tabular-nums font-mono text-white font-bold">
                {tier_axis.reduce((a, t) => a + colTotal(t), 0)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PipelineLaneCard({ pipeline }) {
  const tone = TIER_TONE[pipeline.tier || ""] || TIER_TONE.UNMAPPED;
  const totalMapped = pipeline.stages.reduce((a, s) => a + (s.count || 0), 0);
  const untagged = Math.max(0, (pipeline.total || 0) - totalMapped);

  return (
    <div className={`rounded-xl border p-4 flex flex-col gap-3 ${tone}`} data-testid={`pipeline-lane-${pipeline.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[9.5px] font-mono uppercase tracking-widest opacity-70">
            Pipeline #{pipeline.id} · {pipeline.route}
          </div>
          <div className="text-white font-semibold text-sm">{pipeline.name}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[9.5px] font-mono uppercase tracking-widest opacity-70">Persons</div>
          <div className="text-white font-bold text-lg tabular-nums">{pipeline.total || 0}</div>
        </div>
      </div>

      <div className="space-y-1">
        {pipeline.stages.length === 0 && (
          <div className="text-[11px] text-slate-500">No stages defined for this pipeline.</div>
        )}
        {pipeline.stages.map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-2 text-[11.5px] py-1 border-b border-white/[0.04] last:border-0">
            <div className="min-w-0 flex-1">
              <div className="text-slate-300 truncate">{s.name}</div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">
                #{s.id} · {s.canonical_key || "—"}
              </div>
            </div>
            <div className={`tabular-nums font-mono ${s.count > 0 ? "text-orange-300 font-semibold" : "text-slate-600"}`}>
              {s.count || 0}
            </div>
          </div>
        ))}
      </div>

      {untagged > 0 && (
        <div className="mt-auto flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-amber-300">
          <AlertTriangle className="w-3 h-3" /> {untagged} persons with no StageId
        </div>
      )}
    </div>
  );
}

export default function PipelineWorkflowsPanel({ compact = false }) {
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/pipedrive/counts`);
      setCounts(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load pipeline counts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runSync = async () => {
    if (!window.confirm("PD SYNC will clear all local drag-drop stage overrides so pipelines re-read from the Pipedrive-derived StageId + PipelineId in Mongo.\n\nProceed?")) return;
    setSyncing(true);
    try {
      const { data } = await api.post(`/pipedrive/sync`);
      toast.success(
        `PD SYNC done · ${data.cleared_overrides} deal overrides + ${data.cleared_lead_overrides} lead overrides cleared`,
        { description: `${data.persons_touched} persons touched` }
      );
      await load();
    } catch (e) {
      toast.error("PD SYNC failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSyncing(false);
    }
  };

  const totals = counts?.totals || {};
  const grandTotal = Object.values(totals).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-5" data-testid="pipeline-workflows-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-orange-400 font-mono mb-1 flex items-center gap-1.5">
            <Workflow className="w-3 h-3" /> Pipeline · Live matrix
          </div>
          <h2 className={compact ? "text-xl font-semibold" : "text-2xl font-display font-bold tracking-tight"}>
            Pipeline Workflows
          </h2>
          <p className="text-slate-400 text-[13px] mt-1">
            {loading ? "Loading…" : `${grandTotal} persons across ${counts?.pipelines?.length || 0} Pipedrive pipelines · ${counts?.unmapped || 0} unmapped`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={runSync}
            disabled={syncing || loading}
            data-testid="workflow-pd-sync"
            title="Clear all local stage overrides and re-derive from Mongo PipelineId + StageId"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-orange-500/40 bg-orange-500/[0.08] text-sm text-orange-200 hover:bg-orange-500/[0.14] disabled:opacity-50"
          >
            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            PD SYNC
          </button>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="workflow-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 bg-white/[0.03] text-sm text-slate-300 hover:text-white hover:bg-white/[0.06] disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 text-rose-200 text-sm px-3 py-2">
          {error}
        </div>
      )}

      {loading && (
        <div className="p-8 text-center text-slate-500 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading matrix…
        </div>
      )}

      {!loading && counts && (
        <>
          {/* Matrix */}
          <StageMatrix counts={counts} />

          {/* Lane cards */}
          <div className="flex items-center gap-3 pt-2">
            <div className="text-[10px] uppercase tracking-widest text-purple-400 font-mono flex items-center gap-1.5">
              <GitBranch className="w-3 h-3" /> Pipeline lanes
            </div>
            <div className="flex-1 h-px bg-white/[0.06]" />
            <Link
              to="/deals"
              className="text-[10.5px] font-mono uppercase tracking-widest text-slate-400 hover:text-white inline-flex items-center gap-1"
              data-testid="workflow-open-deals"
            >
              Open Deal Pipeline <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
          <div className={`grid grid-cols-1 md:grid-cols-2 ${compact ? "" : "xl:grid-cols-3"} gap-4`}>
            {counts.pipelines.map((p) => <PipelineLaneCard key={p.id} pipeline={p} />)}
          </div>

          {counts.as_of && (
            <div className="text-[10.5px] text-slate-600 font-mono">
              As of {new Date(counts.as_of).toLocaleString()}
            </div>
          )}
        </>
      )}
    </div>
  );
}
